import { keyframes, styled } from "goober";
import { useState } from "react";
import { ClickButton } from "./ClickButton";
import { ShortcutHint } from "./ShortcutHint";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const scribbleDraw = keyframes`
  0% {
    stroke-dashoffset: 1;
    opacity: 0.25;
  }
  15% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
`;

const Button = styled(ClickButton)`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 8px 0 6px;
  height: 20px;
  border-radius: 38px;
  background: var(--alpha4);
  color: var(--text-secondary);
  opacity: 0.68;
  &:hover {
    background: var(--alpha8);
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 1px;
  }
  &[data-on="true"] {
    opacity: 1;
    color: var(--text-primary);
  }
`;

const Dot = styled("span")`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  flex-shrink: 0;
`;

const Scribble = styled("svg")`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 12px;
  height: 12px;
  overflow: visible;
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

const ScribblePath = styled("path")`
  fill: none;
  stroke: var(--text-secondary);
  stroke-opacity: 0.38;
  stroke-width: 1.08;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 0;
  opacity: 0.9;
`;

const ActiveScribblePath = styled(ScribblePath)`
  stroke: var(--purple-600);
  stroke-opacity: 1;
  opacity: 1;
  [data-activating="true"] & {
    stroke-dashoffset: 1;
    animation: ${scribbleDraw} 460ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
`;

const scribblePath =
  "M2.1 6.8 C1.6 4.3 3.5 2.2 6.1 2.2 C9.1 2.2 10.7 4.8 9.5 7.4 C8.2 10.2 4.5 10.3 2.9 7.8 C1.6 5.7 3.3 3.3 6 3.4 C8.2 3.5 9.1 5.5 7.9 7.1 C6.8 8.6 4.7 8.2 4.4 6.7 C4.1 5.5 5.3 4.8 6.4 5.3 C7.1 5.7 7 6.6 6.2 6.8";

type Props = {
  planMode: boolean;
  onToggle: () => void;
};

export function PlanModeToggle({ planMode, onToggle }: Props) {
  const [activating, setActivating] = useState(false);
  const [wasOn, setWasOn] = useState(planMode);
  if (planMode !== wasOn) {
    setWasOn(planMode);
    // Animate on the off→on edge; the scribble's onAnimationEnd clears it.
    if (planMode) setActivating(true);
    else setActivating(false);
  }

  return (
    <Tooltip
      content={
        <>
          Plan before 3B takes action <ShortcutHint>⇧⌥P</ShortcutHint>
        </>
      }
    >
      <Button
        data-on={planMode}
        aria-pressed={planMode}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onToggle}
      >
        <Dot data-activating={activating || undefined}>
          <Scribble viewBox="0 0 12 12" aria-hidden>
            <ScribblePath pathLength={1} d={scribblePath} />
            {planMode && (
              <ActiveScribblePath
                pathLength={1}
                d={scribblePath}
                onAnimationEnd={() => {
                  setActivating(false);
                }}
              />
            )}
          </Scribble>
        </Dot>
        <Text variant="text-small-md">Plan</Text>
      </Button>
    </Tooltip>
  );
}
